import {
    FlatOperation,
    HttpMethod,
    OpenApiOperation,
    OpenApiParameter,
    OpenApiSchema,
    OpenApiSpec,
    OperationGroup,
} from './types';

const HTTP_METHODS: HttpMethod[] = [
    'get',
    'put',
    'post',
    'delete',
    'patch',
    'options',
    'head',
    'trace',
];

const UNTAGGED_GROUP = 'Other';

/**
 * Flatten an OpenAPI `paths` object into a tag-grouped list of operations.
 * Operations are bucketed by their first tag (or "Other" when untagged), and
 * groups are ordered to match the spec's top-level `tags` declaration first,
 * with any remaining tags appended alphabetically.
 */
export const groupOperationsByTag = (
    spec: OpenApiSpec | null | undefined,
): OperationGroup[] => {
    if (!spec || !spec.paths) {
        return [];
    }

    const groups = new Map<string, FlatOperation[]>();

    Object.entries(spec.paths).forEach(([path, pathItem]) => {
        if (!pathItem) {
            return;
        }

        HTTP_METHODS.forEach(method => {
            const operation = pathItem[method] as OpenApiOperation | undefined;
            if (!operation) {
                return;
            }

            const tag =
                operation.tags && operation.tags.length > 0
                    ? operation.tags[0]
                    : UNTAGGED_GROUP;

            const bucket = groups.get(tag) ?? [];
            bucket.push({ method, path, operation });
            groups.set(tag, bucket);
        });
    });

    const declaredOrder = (spec.tags ?? []).map(t => t.name);
    const tagDescriptions = new Map<string, string | undefined>(
        (spec.tags ?? []).map(t => [t.name, t.description]),
    );

    const presentTags = Array.from(groups.keys());
    const orderedTags = [
        ...declaredOrder.filter(t => groups.has(t)),
        ...presentTags
            .filter(t => !declaredOrder.includes(t))
            .sort((a, b) => a.localeCompare(b)),
    ];

    return orderedTags.map(tag => ({
        tag,
        description: tagDescriptions.get(tag),
        operations: (groups.get(tag) ?? []).sort((a, b) =>
            a.path.localeCompare(b.path),
        ),
    }));
};

/** Color hint per HTTP method for the method badge. */
export const methodColor = (method: HttpMethod): string => {
    switch (method) {
        case 'get':
            return 'blue';
        case 'post':
            return 'green';
        case 'put':
            return 'orange';
        case 'patch':
            return 'gold';
        case 'delete':
            return 'red';
        default:
            return 'default';
    }
};

/**
 * Produce a short, human-readable type label for a schema without resolving
 * `$ref`s into full objects (we only surface the referenced name).
 */
export const describeSchema = (
    schema: OpenApiSchema | undefined | null,
): string => {
    if (!schema) {
        return 'unknown';
    }

    if (schema.$ref) {
        return refName(schema.$ref);
    }

    if (schema.type === 'array') {
        return `${describeSchema(schema.items)}[]`;
    }

    if (schema.enum && schema.enum.length > 0) {
        return `enum(${schema.enum.map(String).join(', ')})`;
    }

    if (schema.format) {
        return `${schema.type ?? 'object'} <${schema.format}>`;
    }

    return schema.type ?? 'object';
};

/** Extract the trailing name from a `$ref` like `#/components/schemas/Foo`. */
export const refName = (ref: string): string => {
    const parts = ref.split('/');
    return parts[parts.length - 1] || ref;
};

/** Merge path-level and operation-level parameters (operation wins on name). */
export const collectParameters = (
    pathLevel: OpenApiParameter[] | undefined,
    operationLevel: OpenApiParameter[] | undefined,
): OpenApiParameter[] => {
    const byName = new Map<string, OpenApiParameter>();
    (pathLevel ?? []).forEach(p => {
        if (p?.name) byName.set(`${p.in}:${p.name}`, p);
    });
    (operationLevel ?? []).forEach(p => {
        if (p?.name) byName.set(`${p.in}:${p.name}`, p);
    });
    return Array.from(byName.values());
};
